import { useState, useRef, useCallback, useMemo, useContext, createContext } from 'react'
import ConfirmModal from '../../components/confirm-modal'

const ModalContext = createContext<IModalContext>({} as IModalContext)

interface IModalContext {
    modal: ModalState
    openModal: (title: string, text: string, onConfirm: any) => void
    closeModal: () => void
    confirmModal: () => void
}

interface ModalState {
    open: boolean
    title: string
    text: string
    onConfirm: () => void
}

export const useConfirmModal = () => {
    const context = useContext(ModalContext)

    const { openModal, closeModal, confirmModal } = context

    return { openModal, closeModal, confirmModal }
}

export const ConfirmModalProvider = ({ children }) => {
    const [modal, setModal] = useState<ModalState>({
        open: false,
        title: '',
        text: '',
        onConfirm: () => {},
    })

    const onConfirmRef = useRef<() => void>(() => {})

    const openModal = useCallback(
        (title: string, text: string, onConfirm: any) => {
            onConfirmRef.current = onConfirm
            setModal({
                open: true,
                title,
                text,
                onConfirm,
            })
        },
        [setModal]
    )

    const closeModal = useCallback(() => {
        setModal(prev => ({ ...prev, open: false }))
    }, [setModal])

    const confirmModal = useCallback(() => {
        onConfirmRef.current()
        closeModal()
    }, [closeModal])

    const value = useMemo(
        () => ({
            modal,
            openModal,
            closeModal,
            confirmModal,
        }),
        [modal, openModal, closeModal, confirmModal]
    )

    return (
        <ModalContext.Provider value={value}>
            {children}
            <ConfirmModal
                open={modal.open}
                title={modal.title}
                text={modal.text}
                onClose={closeModal}
                onConfirm={confirmModal}
            />
        </ModalContext.Provider>
    )
}
